/**
 * Seasonal Produce Widget
 * Displays what's in season using the Seasonal Produce Agent
 */

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { seasonalProduceAgent } from '@/services/agents';
import linkingHelper from '@/utils/linkingHelper';
import { Calendar, Sun, Droplets, Wind, Snowflake, ExternalLink } from 'lucide-react';

interface SeasonalProduceWidgetProps {
  month?: number;
  showPeakOnly?: boolean;
  maxItems?: number;
}

const SeasonalProduceWidget: React.FC<SeasonalProduceWidgetProps> = ({
  month,
  showPeakOnly = false,
  maxItems = 6,
}) => {
  const [seasonalData, setSeasonalData] = useState<any>(null);

  useEffect(() => {
    const recommendations = seasonalProduceAgent.getSeasonalRecommendations(month);
    setSeasonalData(recommendations);
  }, [month]);

  if (!seasonalData) {
    return null;
  }

  const produceToShow = showPeakOnly
    ? seasonalData.peakProduce.slice(0, maxItems)
    : seasonalData.available.slice(0, maxItems);

  const getSeasonIcon = (month: number) => {
    if ([12, 1, 2].includes(month)) return <Snowflake size={20} />;
    if ([3, 4, 5].includes(month)) return <Droplets size={20} />;
    if ([6, 7, 8].includes(month)) return <Sun size={20} />;
    return <Wind size={20} />;
  };

  const getSeasonColor = (month: number) => {
    if ([12, 1, 2].includes(month)) return '#5A9FD4';
    if ([3, 4, 5].includes(month)) return '#85BB2F';
    if ([6, 7, 8].includes(month)) return '#FDB913';
    return '#D4883A';
  };

  return (
    <div style={styles.widget}>
      <div style={styles.header}>
        <div style={styles.headerLeft}>
          <div style={{ color: getSeasonColor(seasonalData.month) }}>
            {getSeasonIcon(seasonalData.month)}
          </div>
          <div>
            <h3 style={styles.title}>What's In Season</h3>
            <p style={styles.subtitle}>{seasonalData.monthName}</p>
          </div>
        </div>
        <Calendar size={18} style={{ color: '#999' }} />
      </div>

      <div style={styles.produceGrid}>
        {produceToShow.map((produce: any, index: number) => {
          const productUrl = linkingHelper.getProductUrl(produce.name);
          const searchUrl = linkingHelper.getSearchUrl(produce.name);

          return (
            <Link
              key={index}
              to={productUrl}
              style={styles.produceCard}
              className="seasonal-produce-card"
            >
              <div style={styles.produceHeader}>
                <div style={styles.produceName}>{produce.name}</div>
                <ExternalLink size={14} style={styles.linkIcon} />
              </div>
              <div style={styles.produceCategory}>
                {produce.category === 'fruit' && '🍎'}
                {produce.category === 'vegetable' && '🥬'}
                {produce.category === 'herb' && '🌿'}
                {produce.category === 'other' && '🌾'}
                <span style={styles.categoryText}>{produce.category}</span>
              </div>
              {produce.nutritionHighlights && produce.nutritionHighlights.length > 0 && (
                <div style={styles.highlights}>
                  {produce.nutritionHighlights.slice(0, 2).map((highlight: string, i: number) => (
                    <span key={i} style={styles.highlightTag}>
                      {highlight}
                    </span>
                  ))}
                </div>
              )}
              <div style={styles.cardActions}>
                <Link
                  to={searchUrl}
                  onClick={(e) => e.stopPropagation()}
                  style={styles.findFarmsLink}
                >
                  Find Farms →
                </Link>
              </div>
            </Link>
          );
        })}
      </div>

      {seasonalData.tips && seasonalData.tips.length > 0 && (
        <div style={styles.tipsSection}>
          <h4 style={styles.tipsTitle}>Seasonal Tips</h4>
          <ul style={styles.tipsList}>
            {seasonalData.tips.slice(0, 2).map((tip: string, index: number) => (
              <li key={index} style={styles.tipItem}>
                {tip}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

const styles = {
  widget: {
    backgroundColor: '#fff',
    borderRadius: '16px',
    padding: '24px',
    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
    marginBottom: '20px',
  } as React.CSSProperties,
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px',
  } as React.CSSProperties,
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  } as React.CSSProperties,
  title: {
    fontSize: '18px',
    fontWeight: '700',
    color: '#1a1a1a',
    margin: 0,
  } as React.CSSProperties,
  subtitle: {
    fontSize: '13px',
    color: '#999',
    margin: '4px 0 0 0',
  } as React.CSSProperties,
  produceGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '12px',
    marginBottom: '20px',
  } as React.CSSProperties,
  produceCard: {
    padding: '16px',
    backgroundColor: '#F9FAF8',
    borderRadius: '12px',
    border: '1px solid #E8EDE7',
    transition: 'all 0.2s ease',
    textDecoration: 'none',
    display: 'block',
    cursor: 'pointer',
    position: 'relative',
  } as React.CSSProperties,
  produceHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '6px',
  } as React.CSSProperties,
  linkIcon: {
    color: '#999',
    opacity: 0.6,
  } as React.CSSProperties,
  produceName: {
    fontSize: '15px',
    fontWeight: '600',
    color: '#1a1a1a',
    marginBottom: '6px',
  } as React.CSSProperties,
  produceCategory: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '12px',
    color: '#666',
    marginBottom: '10px',
  } as React.CSSProperties,
  categoryText: {
    textTransform: 'capitalize' as const,
  } as React.CSSProperties,
  highlights: {
    display: 'flex',
    flexWrap: 'wrap' as const,
    gap: '6px',
  } as React.CSSProperties,
  highlightTag: {
    fontSize: '10px',
    padding: '3px 8px',
    backgroundColor: '#2D5A27',
    color: '#fff',
    borderRadius: '10px',
    fontWeight: '500',
  } as React.CSSProperties,
  tipsSection: {
    paddingTop: '16px',
    borderTop: '1px solid #E8EDE7',
  } as React.CSSProperties,
  tipsTitle: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#1a1a1a',
    marginBottom: '10px',
  } as React.CSSProperties,
  tipsList: {
    margin: 0,
    paddingLeft: '20px',
  } as React.CSSProperties,
  tipItem: {
    fontSize: '13px',
    color: '#666',
    marginBottom: '6px',
    lineHeight: '1.5',
  } as React.CSSProperties,
  cardActions: {
    marginTop: '10px',
    paddingTop: '10px',
    borderTop: '1px solid #E8EDE7',
  } as React.CSSProperties,
  findFarmsLink: {
    fontSize: '12px',
    color: '#2D5A27',
    fontWeight: '600',
    textDecoration: 'none',
    display: 'inline-flex',
    alignItems: 'center',
    transition: 'color 0.2s',
  } as React.CSSProperties,
};

export default SeasonalProduceWidget;
